/**
 * The **client-safe** half of the phone helpers (TRY-315).
 *
 * ⚠️ **This module must stay importable from a `"use client"` file, and it must
 * not import `libphonenumber-js`.** It may import nothing at all — no
 * `server-only`, no `node:*`, no prisma, no other `@/lib` module. `PhoneInput`
 * (`src/components/ui/phone-input.tsx`) is a client component and reaches
 * `isPhoneShape` from here; pulling the library in would put its metadata into
 * every bundle that renders a phone field — including the storefront checkout,
 * which TRY-350 converted, and where the cost would land on a customer rather
 * than on a merchant with the panel already loaded. Both storefront call sites
 * take the dialling code as a **server-computed prop** for exactly this reason
 * (`PhoneOtpGate`'s `dialCode` docblock says it again at the call site).
 *
 * Everything that needs real numbering-plan knowledge lives in
 * `@/lib/phone-normalize`, which is imported only from server code. The split is
 * the whole design: a loose, country-agnostic shape check here, and the authority
 * there. See `phoneField`'s docblock in `src/lib/validation/fields.ts` for why the
 * asymmetry is deliberate rather than a gap.
 */

/**
 * The dialling code shown when nothing better is known: Bahrain.
 *
 * ⚠️ A literal, not `getCountryCallingCode(DEFAULT_COUNTRY)`, because that call
 * needs the library's metadata and this module may not import it.
 * `scripts/assert-phone-metadata.ts` asserts the two agree, so the duplication
 * cannot drift silently — which is the only reason a hand-written copy of library
 * data is acceptable here.
 */
export const DEFAULT_DIAL_CODE = "973";

/**
 * One entry in `PhoneInput`'s optional country picker (TRY-478).
 *
 * ⚠️ **A type, not data.** It lives in the client-safe half only so that
 * `ui/phone-input.tsx` can name the shape without importing
 * `@/lib/phone-normalize` — the values are always built there, by
 * `shopperPhoneHints`, from `libphonenumber-js`. Hand-writing a dialling code
 * into a client file is the duplication `DEFAULT_DIAL_CODE` above is the single
 * sanctioned instance of; do not add a second.
 */
export interface PhoneCountryOption {
	/** ISO alpha-2. The value the picker posts, and what `shopperCountry` reads back. */
	iso: string;
	/** Dialling code without the `+`, e.g. `"966"`. Display only — nothing is concatenated. */
	dialCode: string;
	/** What to type after it, for the placeholder. Undefined falls back to the message key. */
	example?: string;
}

/**
 * Arabic-Indic (٠-٩) and Eastern Arabic-Indic (۰-۹) digits folded to ASCII.
 *
 * ⚠️ **Required, not a nicety.** Arabic is this product's default locale, and
 * `\D` matches ٣ — so without this fold every regex here strips an
 * Arabic-Indic number to nothing. A merchant typing ٣٦٠٦٨٦٢٢ used to get
 * `normalizePhone(...) === null` and, once `phoneField` existed, a rejection
 * whose own message is written in Western digits. Fold first, then count.
 *
 * (`libphonenumber-js` folds these itself, via its own `parseDigits`. This copy
 * exists because the check below must not import the library — see the module
 * docblock. It is not a second implementation of the *normalizer*, which was the
 * duplication that actually cost something.)
 */
export function toWesternDigits(raw: string): string {
	// Spelled as escapes, not as literal ٠-٩: `regexp/no-obscure-range` rejects
	// the literal form, and rightly — the two blocks are visually near-identical
	// and sit 144 code points apart, so a literal range is unreviewable.
	return raw.replace(/[\u0660-\u0669\u06F0-\u06F9]/g, (c) => String(c.codePointAt(0)! & 0xf));
}

/** Fold non-ASCII numerals, then keep only digits. Nothing else. */
export function phoneDigits(raw: string): string {
	return toWesternDigits(raw).replace(/\D/g, "");
}

/**
 * The digits that carry meaning: {@link phoneDigits} minus any leading zeros —
 * a trunk `0`, an international `00`, or the run of them a mangled paste leaves.
 *
 * ⚠️ **This is the single copy of the leading-zero rule *as this repo applies
 * it*** — `isPhoneShape` below and `normalizePhone`'s international fallback.
 * Counting digits before the strip in one place while expanding after it in
 * another is what once produced a client that rejected `+0000000501234567` while
 * the server accepted it as `973501234567`.
 *
 * ⚠️ This rule's job is the *input* side: what a human types, `00`-prefixed or
 * trunk-zeroed or pasted with a mangled run of zeros. A stored `+E.164` value
 * (TRY-353) reads back as itself for a different reason: the leading `+` makes the
 * parse international and the default country is ignored.
 *
 * ⚠️ **It is deliberately NOT applied on `normalizePhone`'s national branch.** A trunk zero
 * is a per-country fact — `0` for the UAE, absent elsewhere — so that branch hands
 * the raw string to `libphonenumber-js` and lets the library's own national-prefix
 * rule strip it. Pre-stripping here would defeat exactly the knowledge the library
 * was brought in for.
 */
export function phoneSignificantDigits(raw: string): string {
	return phoneDigits(raw).replace(/^0+/, "");
}

/** The digit-count bounds a phone number's *shape* must satisfy. */
const MIN_SHAPE_DIGITS = 7;
const MAX_SHAPE_DIGITS = 15;

/**
 * Shape only: 7–15 significant digits. Deliberately **country-agnostic**, and
 * deliberately looser than `libphonenumber-js`.
 *
 * ⚠️ **Why it is not `isPossiblePhoneNumber` / `isValidPhoneNumber`.** Both of
 * those need a country, and the two callers cannot supply one honestly:
 *
 *   - `phoneField()` is a shared schema builder used by **11 fields across 8
 *     modules** with no tenant in scope (`grep -rn "phoneField({" src`, filtering
 *     the two docblock mentions in `validation/json.ts`; it read "nine" from
 *     TRY-315 until TRY-354, which added the eleventh in
 *     `(shop)/account/addresses/actions.ts` and re-counted rather than
 *     incrementing — nothing gates this figure). Defaulting it to Bahrain would
 *     reject the perfectly valid
 *     UAE `0501234567` — measured: `isPossiblePhoneNumber("0501234567", "BH")` is
 *     `false`, `TOO_LONG`.
 *   - `PhoneInput`'s `nativeValidation` guard *does* know the store's country, and
 *     still must not use it. The rule is that the guard must never block what the
 *     server would take — that is the one direction it must never run in.
 *     ⚠️ **The worked example this bullet used to give is no longer true, and the
 *     replacement is narrower.** It read "a Bahraini store legitimately stores a
 *     Saudi staff number typed in Saudi local form"; TRY-361 made the staff
 *     writer refuse exactly that (`normalizePhone("0512345678")` is `null`), so
 *     the guard is no longer the thing standing between that input and storage.
 *     What still holds: a bare Bahraini `36068622` clears this filter AND
 *     normalizes, so pinning the guard to a country would block a submit the
 *     server accepts. Keep it loose for that case, not for the Saudi one.
 *
 * So this stays a filter — it rejects `"hello"`, `"123"` and a 16-digit paste,
 * which is the whole gap it exists to close — and `normalizePhone` decides what
 * is actually dialable. 15 is E.164's maximum; 7 is below the shortest national
 * number in any currency this product maps.
 *
 * ⚠️ It is **not** the whole of `phoneField`, which also applies `.max(n)`, so
 * the guard built on it is strictly weaker than the field. The two guarded call
 * sites carry a matching `maxLength`; a third must too.
 */
export function isPhoneShape(raw: string): boolean {
	const digits = phoneSignificantDigits(raw);
	return digits.length >= MIN_SHAPE_DIGITS && digits.length <= MAX_SHAPE_DIGITS;
}
